<template>
  <div>
    <v-text-field
      v-if="users.length > 10"
      v-model="searchText"
      :label="searchLabel"
      class="mb-2 pt-0"
      hide-details
      clearable
      style="max-width: 240px"
      @keyup.esc="searchText = ''"
    />
    <!-- No Users managed yet -->
    <div v-if="!users.length">
      {{ $t('userManagementNoUsersYet') }}
    </div>
    <!--
	  Managers don't see themselves, but can manage anyone else. Managed
	  Users do see themselves, but can't modify anything.
    -->
    <v-list
      class="list-tile-auto-height"
      data-cy="usermanagement-users-list"
    >
      <!-- Headers -->
      <v-list-tile
        v-if="$vuetify.breakpoint.mdAndUp"
        class="mb-2"
      >
        <v-list-tile-title>
          <v-layout class="grey--text text--darken-2 body-2">
            <v-layout style="flex: 1">
              {{ $t('userManagementListHeaderUser') }}
            </v-layout>
            <v-layout style="flex: 0.33">
              <span v-if="$store.getters.isUsermanagerOrManagedCoadmin">{{
                $t('userManagementListHeaderLastActive')
              }}</span>
            </v-layout>
            <v-layout style="flex: 0.2">
              <span
                v-if="$store.getters.isUsermanagerOrManagedCoadmin"
                :title="$t('twoFactorAuthentication')"
              >{{ $t('userManagementListHeaderTwoFactorAuth') }}</span>
            </v-layout>
            <v-layout style="flex: 1">
              {{ $t('userManagementListHeaderPermissions') }}
            </v-layout>
          </v-layout>
        </v-list-tile-title>
      </v-list-tile>
      <v-divider />
      <!-- Rows -->
      <template v-for="(user, userIndex) in filteredUsers">
        <v-list-tile
          :key="user.id"
          :to="'/users/manage/' + user.id"
          :disabled="!viewableUsersForCurrentUserLookup[user.id]"
        >
          <v-list-tile-avatar>
            <user-avatar-image
              v-if="user && user.clientprofile.avatar_image_url"
              :user="user"
              :size="40"
            />
            <user-initials
              v-else
              :user="user"
              :color="userColors[userIndex]"
            />
          </v-list-tile-avatar>
          <v-list-tile-title
            class="no-disabled-title-color user-selectable auto-height py-2"
          >
            <v-layout align-center>
              <v-layout
                column
                justify-center
                fill-height
                nowrap
                style="overflow: hidden; flex: 1"
              >
                <v-layout
                  align-center
                  wrap
                >
                  <!-- Firstname & lastname -->
                  <div>
                    {{ user.first_name.trim() || '?' }}
                    <strong>{{ user.last_name.trim() || '?' }}</strong>
                  </div>
                  <!-- Usermanager/Coadmin chip -->
                  <v-chip
                    v-if="
                      user.clientprofile.is_managed_coadmin ||
                        user.clientprofile.is_usermanager
                    "
                    small
                    class="white--text body-2 ml-2"
                    :class="[
                      $store.getters.isUsermanagerOrManagedCoadmin &&
                        user.clientprofile.is_managed_coadmin
                        ? 'blue'
                        : 'msr',
                    ]"
                  >
                    <span v-if="user.clientprofile.is_usermanager">
                      {{ $t('workspaceManager') }}
                    </span>
                    <span v-else>{{
                      $t('userManagementCoadminSwitchLabel')
                    }}</span>
                  </v-chip>
                </v-layout>
                <div class="grey--text text--darken-1 caption">
                  {{ user.email }}
                </div>
                <strong
                  v-if="user.id == $user.id"
                  class="msr--text nowrap"
                >{{
                  $t('yourOwnAccount')
                }}</strong>
              </v-layout>
              <!-- Last active -->
              <div
                v-if="$vuetify.breakpoint.mdAndUp"
                :title="
                  $t('lastActive') +
                    ': ' +
                    managedUserIdToFormattedLastActive[user.id]
                "
                class="ml-auto grey--text caption"
                style="display: flex; flex: 0.33"
              >
                <span
                  v-if="
                    user.last_active &&
                      $store.getters.isUsermanagerOrManagedCoadmin
                  "
                >{{ user.last_active | dayjsFromNow }}</span>
              </div>
              <!-- Twofactor Authentication enabled -->
              <div
                v-if="$vuetify.breakpoint.mdAndUp"
                class="grey--text caption"
                style="display: flex; flex: 0.2"
              >
                <img
                  v-if="user.clientprofile.twofactorauth_enabled == 'sms'"
                  :src="IMAGE_TWO_FACTOR_AUTH_VIA_SMS"
                  height="36px"
                  :title="$t('userManagementUserTwoFactorAuthViaSMS')"
                >
                <img
                  v-else-if="user.clientprofile.twofactorauth_enabled == 'otp'"
                  :src="IMAGE_TWO_FACTOR_AUTH_VIA_APP"
                  height="36px"
                  :title="$t('userManagementUserTwoFactorAuthViaApp')"
                >
              </div>
              <!-- Mini Overview -->
              <v-slide-y-reverse-transition>
                <v-layout
                  v-if="$vuetify.breakpoint.mdAndUp"
                  style="flex: 1"
                >
                  <user-management-admin-hint
                    v-if="responsibleUsersForCurrentUserLookup[user.id]"
                    :user="user"
                  />
                  <user-accesses-mini-overview
                    v-else-if="
                      user.id === $user.id ||
                        viewableUsersForCurrentUserLookup[user.id]
                    "
                    :user="user"
                  />
                </v-layout>
              </v-slide-y-reverse-transition>
              <!-- Keyboard down arrow or placeholder (hidden on mobile) -->
              <v-list-tile-action v-if="$vuetify.breakpoint.mdAndUp">
                <v-icon v-if="viewableUsersForCurrentUserLookup[user.id]">
                  keyboard_arrow_down
                </v-icon>
              </v-list-tile-action>
            </v-layout>
          </v-list-tile-title>
        </v-list-tile>
        <v-divider
          v-if="userIndex < filteredUsers.length - 1"
          :key="'divider' + userIndex + user.id"
        />
      </template>
    </v-list>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import distinctColors from 'distinct-colors';

import {
  IMAGE_TWO_FACTOR_AUTH_VIA_APP,
  IMAGE_TWO_FACTOR_AUTH_VIA_SMS,
} from '@/store';
import matchesFilter from '@/matchesFilter';

import UserAccessesMiniOverview from '@/components/UserManagement/UserAccessesMiniOverview';
import UserAvatarImage from '@/components/UserAvatarImage';
import UserInitials from '@/components/UserManagement/UserInitials';
import UserManagementAdminHint from '@/components/UserManagement/UserManagementAdminHint';

export default {
  components: {
    UserAccessesMiniOverview,
    UserAvatarImage,
    UserInitials,
    UserManagementAdminHint,
  },
  data() {
    return {
      IMAGE_TWO_FACTOR_AUTH_VIA_APP: IMAGE_TWO_FACTOR_AUTH_VIA_APP,
      IMAGE_TWO_FACTOR_AUTH_VIA_SMS: IMAGE_TWO_FACTOR_AUTH_VIA_SMS,
      searchText: '',
    };
  },
  computed: {
    /**
     * This list is a combination of the current User and managed Users.
     *
     * The backend already sorts this, no need to do it again here. We
     * also want to avoid frontend sorting based on name as we allow
     * renaming Users here, which could reorder them when we only have
     * one Users expanded, which would be confusing.
     */
    users() {
      const ourself = this.$user;
      return [ourself].concat(this.$store.state.common.managedUsers);
    },
    filteredUsers() {
      if (!this.searchText) {
        return this.users;
      }

      const search = this.searchText.toLowerCase();
      return this.users.filter((user) => {
        return (
          matchesFilter(user.first_name, search) ||
          matchesFilter(user.last_name, search) ||
          matchesFilter(user.username, search) ||
          matchesFilter(user.email, search)
        );
      });
    },
    searchLabel() {
      const suffix = this.searchText
        ? ' (' + this.filteredUsers.length + ')'
        : '';
      return this.$t('search') + suffix + ' ...';
    },
    userColors() {
      return distinctColors({
        count: this.users.length,
        lightMin: 20,
        chromaMin: 30,
      });
    },
    /**
     * Helper to check whether a User is responsible for the current one.
     *
     * 'Responsible' is e.g. every usermanager or coadmin when the
     * current user is just a normal one. For coadmins, only the
     * usermanager is responsible.
     */
    responsibleUsersForCurrentUserLookup() {
      function isResponsibleForCurrentUser(currentUser, user) {
        if (currentUser.clientprofile.is_usermanager) {
          return false;
        }
        if (currentUser.id === user.id) {
          return false;
        }
        if (currentUser.clientprofile.is_managed_coadmin) {
          return user.clientprofile.is_usermanager;
        }
        return (
          user.clientprofile.is_usermanager ||
          user.clientprofile.is_managed_coadmin
        );
      }

      const lookup = {};
      this.$store.state.common.managedUsers.forEach((user) => {
        lookup[user.id] = isResponsibleForCurrentUser(this.$user, user);
      });
      return lookup;
    },
    /**
     * Helper to check whether a User can be detail-viewed by the current one.
     *
     * E.g. only a usermanager/coadmin can edit other users, but only those
     * of a lower role.
     */
    viewableUsersForCurrentUserLookup() {
      const lookup = {};
      lookup[this.$user.id] = true;
      if (!this.$store.getters.isUsermanagerOrManagedCoadmin) {
        return lookup;
      }
      this.$store.state.common.managedUsers.forEach((user) => {
        const allowedToView =
          this.$store.getters.isUsermanager ||
          (this.$store.getters.isManagedCoadmin &&
            !user.clientprofile.is_managed_coadmin &&
            !user.clientprofile.is_usermanager);
        if (allowedToView) {
          lookup[user.id] = true;
        }
      });
      return lookup;
    },
    managedUserIdToFormattedLastActive() {
      const userIdToDate = {};
      this.users.forEach((user) => {
        if (!user.last_active) {
          return;
        }
        userIdToDate[user.id] = dayjs(user.last_active).format('DD.MM.YYYY');
      });
      return userIdToDate;
    },
  },
};
</script>

<style>
.v-list__tile--disabled .v-list__tile__title.user-selectable {
  user-select: auto !important;
}

.v-list__tile--disabled .v-list__tile__title.no-disabled-title-color {
  color: initial !important;
}
</style>



// WEBPACK FOOTER //
// src/components/UserManagement/ManageUsers.vue